import {
  Driver,
  TranslatedDrivingExperience,
  Vehicle,
} from '../../types/car-insurance/interface';
import { getDateOfBirth } from './helpers';

const defaultDriverMM: Driver = {
  dateOfBirth: getDateOfBirth(35),
  age: 35,
  gender: 'M',
  maritalStatus: 'M',
  totalDrivingExperience: 3,
  numberOfClaims: 0,
};

const defaultVehicleMM: Vehicle = {
  makeName: '',
  modelCode: '',
  modelName: '',
  manufactureYear: '',
  trim: '',
  fairMarket: 0,
  // Lowercase: matches carUseType options and Zoho's Vehicle_Type; the premiums
  // request upper-cases it in fe-services (ITSM-80971).
  type: 'private',
};

export const defaultContextStateMM = {
  driver: defaultDriverMM,
  vehicle: defaultVehicleMM,
  ncd: 30,
  prices: {
    propertyDamage: 200000,
    personalAccident: 350000,
    isActsOfNature: true,
  },
};

export const defaultRequestParamsMoneyMax = {
  // No default vehicle: the user picks one (CMS-2047).
  vehicle: { ...defaultVehicleMM, variantCode: '' },
  driver: {
    identityType: 'NRIC',
    dateOfBirth: defaultDriverMM.dateOfBirth,
    age: defaultDriverMM.age,
    gender: defaultDriverMM.gender,
    maritalStatus: defaultDriverMM.maritalStatus,
    totalDrivingExperience: defaultDriverMM.totalDrivingExperience,
    numberOfClaims: defaultDriverMM.numberOfClaims,
  },
  ncd: defaultContextStateMM.ncd,
  prices: {
    propertyDamage: 200000,
    personalAccident: 350000,
    isActsOfNature: true,
  },
};

export const nextButtonStyles =
  'w-full h-9 flex items-center gap-1 justify-center bg-[#04709B] text-white rounded-lg text-base font-semibold cursor-pointer';

export const disabledButton =
  'w-full h-9 flex items-center justify-center bg-gray-400 text-white rounded-lg text-base font-semibold cursor-not-allowed';

// Values must match the canonical lowercase set used by the legacy CI page and
// expected by Zoho's Vehicle_Type field (ITSM-80971). They also match the
// default vehicle.type ('private'), so the correct tab is preselected.
export const carUseType = [
  { label: 'Private', value: 'private' },
  { label: 'Grab / TNVS', value: 'commercial' },
  { label: 'Other', value: 'other' },
];

export const monthOptions = [
  { label: 'September', value: '8' },
  { label: 'October', value: '9' },
  { label: 'November', value: '10' },
  { label: 'December', value: '11' },
  { label: 'January', value: '0' },
  { label: 'February', value: '1' },
  { label: 'March', value: '2' },
  { label: 'April', value: '3' },
  { label: 'May', value: '4' },
  { label: 'June', value: '5' },
  { label: 'July', value: '6' },
  { label: 'August', value: '7' },
];

export const enquiryPurposeOptions = [
  { label: 'How to Apply', value: 'How to Apply' },
  { label: 'Getting a quotation', value: 'Getting a quotation' },
  { label: 'Insurance Product', value: 'Insurance Product' },
  { label: 'Advise', value: 'Advise' },
];

export const contactMethodIconOptions = [
  { label: 'whatsapp', value: 'WA' },
  { label: 'phone', value: 'Phone' },
  { label: 'email', value: 'Email' },
];

export const errorInput = [
  { field: 'name', error: 'Please enter a valid name' },
  { field: 'email', error: 'Please enter a valid email address' },
  { field: 'phone', error: 'Please enter a valid phone number' },
];

export const genderOptions = (translations: {
  gender_male: string;
  gender_female: string;
}) => [
  { label: translations.gender_male, value: 'M' },
  { label: translations.gender_female, value: 'F' },
];

export const maritalStatus = (translations: {
  marital_status_single: string;
  marital_status_married: string;
}) => [
  { label: translations.marital_status_single, value: 'S' },
  { label: translations.marital_status_married, value: 'M' },
];

export const occupationType = (translations: {
  indoor: string;
  outdoor: string;
}) => [
  { label: translations.indoor, value: 'indoor' },
  { label: translations.outdoor, value: 'outdoor' },
];

export const yesNoOptions = (translations: { yes: string; no: string }) => [
  { label: translations.yes, value: 'Y' },
  { label: translations.no, value: 'N' },
];

export const drivingExperience = (
  translations: TranslatedDrivingExperience,
) => [
  { label: translations.zero_years, value: '0' },
  { label: translations.one_year, value: '1' },
  { label: translations.two_years, value: '2' },
  { label: translations.three_years_and_above, value: '3' },
];

export const bolttechDrivingExperience = (
  translations: TranslatedDrivingExperience,
) => [
  { label: translations.zero_years, value: '0' },
  { label: translations.one_year, value: '1' },
  { label: translations.two_years, value: '2' },
  { label: translations.three_years, value: '3' },
  { label: translations.four_years, value: '4' },
  { label: translations.five_years_and_above, value: '5' },
];

export const numberOfClaims = [
  { label: '0', value: '0' },
  { label: '1', value: '1' },
  { label: '2', value: '2' },
];

export const bolttechNumberOfClaims = [
  { label: '0', value: '0' },
  { label: '1', value: '1' },
  { label: '2', value: '2' },
  { label: '3', value: '3' },
];

export const properyDamageValues = [
  50000, 75000, 100000, 150000, 200000, 250000, 300000, 400000, 500000, 750000,
  1000000,
];
export const personalAccidentValues = [
  50000, 75000, 100000, 150000, 200000, 250000, 300000, 350000, 400000, 450000,
  500000, 550000, 600000, 650000, 700000, 750000, 800000, 850000, 900000,
  950000, 1000000,
];
